import API_BASE from '../../utils/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import getProfilePictureSrc from '../../utils/profilePicture';

const PAGE_SIZE = 10;

const ROLE_BADGE = {
  admin:        'bg-purple-100 text-purple-800',
  principal:    'bg-red-100 text-red-800',
  class_teacher:'bg-green-100 text-green-800',
  parent:       'bg-blue-100 text-blue-800',
  super_admin:  'bg-gray-100 text-gray-800',
};

const ROLE_LABELS = {
  admin: 'Admin',
  principal: 'Principal',
  class_teacher: 'Class Teacher',
  parent: 'Parent',
};

const emptyForm = {
  name: '', email: '', password: '', role: 'class_teacher', employeeId: '', phone: '', address: ''
};

const Avatar = ({ user }) => (
  user.profilePicture ? (
    <img
      src={getProfilePictureSrc(user.profilePicture)}
      alt={user.name}
      className="w-9 h-9 rounded-full object-cover border"
    />
  ) : (
    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700 shrink-0">
      {user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
    </div>
  )
);

const FormField = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ admin: 0, principal: 0, class_teacher: 0, parent: 0 });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const debounceRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [generatingId, setGeneratingId] = useState(false);

  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  const fetchUsers = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (roleFilter) params.append('role', roleFilter);
      const res = await axios.get(`${API_BASE}/users?${params}`);
      const data = res.data;
      const list = Array.isArray(data.users) ? data.users : [];
      setUsers(list);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);

      // Recompute stats from a full count query (no search/role filter)
      const statsRes = await axios.get(`${API_BASE}/users?limit=1`);
      const allRoles = ['admin', 'principal', 'class_teacher', 'parent'];
      const counts = {};
      await Promise.all(allRoles.map(async r => {
        const r2 = await axios.get(`${API_BASE}/users?role=${r}&limit=1`);
        counts[r] = r2.data.total || 0;
      }));
      setStats(counts);
    } catch (_) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, roleFilter, page]);

  useEffect(() => { fetchUsers(page); }, [debouncedSearch, roleFilter, page]);

  const fetchNextEmployeeId = async (role) => {
    if (role === 'parent') return;
    setGeneratingId(true);
    try {
      const res = await axios.get(`${API_BASE}/users/next-employee-id?role=${role}`);
      setFormData(f => ({ ...f, employeeId: res.data.employeeId }));
    } catch (_) {
      // Leave employeeId blank for manual entry if generation fails
    } finally {
      setGeneratingId(false);
    }
  };

  const handleRoleChange = (role) => {
    setFormData(f => ({ ...f, role, employeeId: role === 'parent' ? '' : f.employeeId }));
    if (!editingUser) fetchNextEmployeeId(role);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email';
    if (!editingUser && !formData.password) errs.password = 'Password required for new users';
    if (formData.role !== 'parent' && !formData.employeeId?.trim()) errs.employeeId = 'Employee ID required';
    if (!formData.phone?.trim()) errs.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone)) errs.phone = 'Phone number must be exactly 10 digits';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (editingUser) {
        await axios.put(`${API_BASE}/users/${editingUser._id}`, formData);
        setSuccess('User updated successfully');
      } else {
        await axios.post(`${API_BASE}/users`, formData);
        setSuccess('User created successfully');
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData(emptyForm);
      fetchUsers(page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving user');
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_BASE}/users/${id}`);
      setSuccess('User deleted');
      fetchUsers(page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (_) {
      setError('Error deleting user');
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({ name: u.name || '', email: u.email || '', password: '', role: u.role || 'class_teacher', employeeId: u.employeeId || '', phone: u.phone || '', address: u.address || '' });
    setFormErrors({});
    setShowPassword(false);
    setShowModal(true);
  };

  const handleToggleChat = async (u) => {
    try {
      const res = await axios.patch(`${API_BASE}/users/${u._id}/toggle-chat`);
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, chatEnabled: res.data.chatEnabled } : x));
      setSuccess(`Chat ${res.data.chatEnabled ? 'enabled' : 'disabled'} for ${u.name}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle chat');
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { setError('Min 6 characters'); return; }
    try {
      await axios.put(`${API_BASE}/users/${resetModal._id}/reset-password`, { newPassword });
      setSuccess(`Password reset for ${resetModal.name}`);
      setResetModal(null);
      setNewPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
      setTimeout(() => setError(''), 4000);
    }
  };

  const inputCls = (hasErr) => `w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${hasErr ? 'border-red-400' : 'border-gray-300'}`;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        {/* Navbar */}
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">User Management</h1>
              <p className="text-sm text-gray-500">{total} users in the system</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setEditingUser(null); setFormData(emptyForm); setFormErrors({}); setShowPassword(false); setShowModal(true); fetchNextEmployeeId(emptyForm.role); }}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
              >
                + Add User
              </button>
              <ProfileDropdown />
            </div>
          </div>
        </nav>

        {/* Alerts — fixed toast so they stay visible above the open modal on error */}
        {error && <div className="fixed top-4 right-4 z-[100] max-w-sm p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg shadow-lg text-sm flex justify-between gap-3"><span>{error}</span><button onClick={() => setError('')} className="text-red-400 hover:text-red-600">✕</button></div>}
        {success && <div className="fixed top-4 right-4 z-[100] max-w-sm p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg shadow-lg text-sm">{success}</div>}

        <div className="p-6 space-y-5">
          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { key: 'admin', label: 'Admins', color: 'bg-purple-500', lightColor: 'bg-purple-50', textColor: 'text-purple-700' },
              { key: 'principal', label: 'Principals', color: 'bg-red-500', lightColor: 'bg-red-50', textColor: 'text-red-700' },
              { key: 'class_teacher', label: 'Teachers', color: 'bg-green-500', lightColor: 'bg-green-50', textColor: 'text-green-700' },
              { key: 'parent', label: 'Parents', color: 'bg-blue-500', lightColor: 'bg-blue-50', textColor: 'text-blue-700' },
            ].map(({ key, label, lightColor, textColor }) => (
              <button
                key={key}
                onClick={() => { setRoleFilter(roleFilter === key ? '' : key); setPage(1); }}
                className={`rounded-xl p-4 text-left transition border-2 shadow-sm ${roleFilter === key ? 'border-indigo-400' : 'border-transparent'} ${lightColor}`}
              >
                <p className={`text-3xl font-bold ${textColor}`}>{stats[key] || 0}</p>
                <p className={`text-sm font-medium ${textColor} opacity-80 mt-1`}>{label}</p>
                {roleFilter === key && <p className="text-xs text-indigo-600 mt-1 font-semibold">Filtering ✓</p>}
              </button>
            ))}
          </div>

          {/* Search & Filters */}
          <div className="bg-white rounded-xl shadow p-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, or employee ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>
              )}
            </div>
            <select
              value={roleFilter}
              onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="principal">Principal</option>
              <option value="class_teacher">Class Teacher</option>
              <option value="parent">Parent</option>
            </select>
            {(searchTerm || roleFilter) && (
              <button onClick={() => { setSearchTerm(''); setRoleFilter(''); setPage(1); }} className="text-sm text-indigo-600 hover:underline whitespace-nowrap">
                Clear
              </button>
            )}
          </div>

          {/* User table */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee ID</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Chat</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center">
                        <div className="flex justify-center items-center gap-2 text-gray-400">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                          Loading...
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-14 text-center text-gray-400">
                        <div className="text-4xl mb-2">👥</div>
                        <p className="font-medium">No users found</p>
                        {(debouncedSearch || roleFilter) && <p className="text-sm mt-1">Try adjusting your search or filters</p>}
                      </td>
                    </tr>
                  ) : users.map(u => (
                    <tr key={u._id} className="hover:bg-gray-50 transition">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar user={u} />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{u.name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${ROLE_BADGE[u.role] || 'bg-gray-100 text-gray-700'}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">{u.employeeId || <span className="text-gray-300">—</span>}</td>
                      <td className="px-5 py-4 text-sm text-gray-700">{u.phone || <span className="text-gray-300">—</span>}</td>
                      <td className="px-5 py-4">
                        {u.role === 'class_teacher' ? (
                          <button
                            onClick={() => handleToggleChat(u)}
                            title={u.chatEnabled ? 'Disable chat for this teacher' : 'Enable chat for this teacher'}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${u.chatEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${u.chatEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button onClick={() => handleEdit(u)} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Edit</button>
                          <button onClick={() => { setResetModal(u); setNewPassword(''); setShowNewPassword(false); }} className="text-sm text-amber-600 hover:text-amber-800 font-medium">Reset Pwd</button>
                          {u.role !== 'admin' && (
                            <button onClick={() => handleDelete(u._id)} className="text-sm text-red-600 hover:text-red-800 font-medium">Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} users
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-2 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                  >«</button>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                  >Previous</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let p;
                    if (totalPages <= 5) p = i + 1;
                    else if (page <= 3) p = i + 1;
                    else if (page >= totalPages - 2) p = totalPages - 4 + i;
                    else p = page - 2 + i;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1 text-sm border rounded ${p === page ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-gray-100'}`}
                      >{p}</button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                  >Next</button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="px-2 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                  >»</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <FormField label="Full Name *" error={formErrors.name}>
                <input type="text" required value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} className={inputCls(formErrors.name)} />
              </FormField>
              <FormField label="Email *" error={formErrors.email}>
                <input type="email" required value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} className={inputCls(formErrors.email)} />
              </FormField>
              {!editingUser && (
                <FormField label="Password *" error={formErrors.password}>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={e => setFormData(f => ({ ...f, password: e.target.value }))} className={inputCls(formErrors.password) + ' pr-16'} />
                    <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700">{showPassword ? 'Hide' : 'Show'}</button>
                  </div>
                </FormField>
              )}
              <FormField label="Role *">
                <select required value={formData.role} onChange={e => handleRoleChange(e.target.value)} className={inputCls(false)}>
                  <option value="admin">Admin</option>
                  <option value="principal">Principal</option>
                  <option value="class_teacher">Class Teacher</option>
                  <option value="parent">Parent</option>
                </select>
              </FormField>
              {formData.role !== 'parent' && (
                <FormField label="Employee ID *" error={formErrors.employeeId}>
                  <div className="relative">
                    <input type="text" required value={formData.employeeId} onChange={e => setFormData(f => ({ ...f, employeeId: e.target.value }))} className={inputCls(formErrors.employeeId) + (generatingId ? ' pr-16' : '')} />
                    {generatingId && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Generating…</span>}
                  </div>
                  {!editingUser && <p className="text-xs text-gray-400 mt-1">Auto-generated for the selected role — you can override it.</p>}
                </FormField>
              )}
              <FormField label="Phone *" error={formErrors.phone}>
                <input type="text" required inputMode="numeric" maxLength={10} value={formData.phone} onChange={e => setFormData(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} className={inputCls(formErrors.phone)} />
              </FormField>
              <FormField label="Address">
                <textarea rows={2} value={formData.address} onChange={e => setFormData(f => ({ ...f, address: e.target.value }))} className={inputCls(false)} />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">{editingUser ? 'Update' : 'Create'} User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Reset Password</h3>
              <button onClick={() => setResetModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Resetting password for <strong>{resetModal.name}</strong>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <div className="relative">
                  <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 characters" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-16" />
                  <button type="button" onClick={() => setShowNewPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">{showNewPassword ? 'Hide' : 'Show'}</button>
                </div>
                {newPassword && newPassword.length < 6 && <p className="text-xs text-red-500 mt-1">Minimum 6 characters</p>}
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setResetModal(null)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 text-sm">Cancel</button>
                <button onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 6} className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm">Reset Password</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
